import type { TOrganization } from './organization';
import type { TProgram } from './program';
import type { TRule } from './rule';
import type { TStage } from './stage';
import type { TMember } from './member';
import type { TMedia } from './media';
import type { TUser } from './user';
import type { TLead } from './lead';
import type { TStudent } from './student';
import type { TView } from './view';
import type { TQuestion } from './question';
import type { ValueOf } from './utilities';
import type { TReminderTemplate } from './reminder-template';

export const REMINDER_CHANNELS = {
  whatsapp: 'WHATSAPP',
  email: 'EMAIL',
  sms: 'SMS',
} as const;

export const REMINDER_TYPES = {
  general: 'GENERAL',
  specificDate: 'SPECIFIC_DATE',
  programDateRelated: 'PROGRAM_DATE_RELATED',
  ruleRelated: 'RULE_RELATED',
  otherRules: 'OTHER_RULES',
  dateRule: 'DATE_RULE',
  bulk: 'BULK',
  bulkStage: 'BULK_STAGE',
  bulkLead: 'BULK_LEAD',
  bulkApplication: 'BULK_APPLICATION',
  flightInfo: 'FLIGHT_INFO',
  submissionDate: 'SUBMISSION_DATE',
} as const;

export const REMINDER_TRIGGER_TYPES = {
  before: 'BEFORE',
  on: 'ON',
  after: 'AFTER',
} as const;

export const REMINDER_LOG_STATUSES = {
  pending: 'PENDING',
  sent: 'SENT',
  delivered: 'DELIVERED',
  failed: 'FAILED',
  bounced: 'BOUNCED',
  opened: 'OPENED',
} as const;

export type TReminderLogStatus = ValueOf<typeof REMINDER_LOG_STATUSES>;

/** Delivery / lifecycle status on the reminder record (bulk & scheduled sends). */
export const REMINDER_ENTITY_STATUSES = {
  scheduled: 'SCHEDULED',
  sent: 'SENT',
  failed: 'FAILED',
  completed: 'COMPLETED',
  cancelled: 'CANCELLED',
  pending: 'PENDING',
} as const;

export const REMINDER_FLIGHT_TYPES = {
  destination: 'DESTINATION',
  return: 'RETURN',
} as const;

export const REMINDER_SUBMISSION_DATE_TYPES = {
  startDate: 'START_DATE',
  endDate: 'END_DATE',
} as const;

export type TReminderEntityStatus = ValueOf<typeof REMINDER_ENTITY_STATUSES>;

export type TReminder = {
  id: number;
  rule: TRule;
  flight_type: ValueOf<typeof REMINDER_FLIGHT_TYPES>;
  submission_date_type: ValueOf<typeof REMINDER_SUBMISSION_DATE_TYPES>;
  stage?: TStage;
  stages?: TStage[];
  program: TProgram;
  organization: TOrganization;
  members: TMember[];
  attachments: TMedia[];
  template?: TReminderTemplate;
  created_by: TUser;
  created_by_member: TMember;
  view?: TView;
  /** Recipient user ids when not expanded; full users when embedded. */
  students: TUser[] | number[];
  leads: TLead[] | number[];
  question: TQuestion | number;
  created_at: string;
  updated_at: string;
  name: string;
  content: string;
  subject?: string;
  date: string;
  frequency?: number;
  frequency_count?: number;
  type: ValueOf<typeof REMINDER_TYPES>;
  notification_channel: ValueOf<typeof REMINDER_CHANNELS>;
  trigger_type: ValueOf<typeof REMINDER_TRIGGER_TYPES>;
  variables: string;
  is_rule_achieved: boolean;
  /** For TRUE_FALSE rules: the choices that trigger the reminder. */
  rule_choices?: string[] | null;
  periodic_task: number;
  /** Present on list/detail API; may be absent on nested reminder payloads. */
  recipients_count?: number;
  failed_count?: number;
  sent_count?: number;
  read_count?: number;
  bounced_count?: number;
  delivered_count?: number;
  timezone?: string;
  status?: TReminderEntityStatus | string;
};

export type TReminderLog = {
  id: number;
  reminder: TReminder;
  organization: TOrganization;
  recipient: TUser;
  service: Record<string, unknown>;
  lead: TLead | null;
  student: TStudent | null;
  created_at: string;
  updated_at: string;
  channel: string;
  sender: string;
  message_id: string;
  status: TReminderLogStatus;
  failure_reason: string;
};
